// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * What a satellite sees (owner, 2026-10-24, satellites ↔ catalog plan step
 * 2): the circle on the ground out to its geometric horizon, from its
 * height alone — a geostationary satellite's whole disk, a low orbiter's
 * small ring that sweeps along its track. Taken from the GP position (the
 * sub-satellite point), never from a descriptor's `region` (VPS, 2026-10-24).
 * Instruments see less than the horizon; this is the most, honestly named.
 *
 * An instrument sees less than the horizon: when the entry names one with
 * a known swath (`SWATH_KM`), the circle is the swath's half-width instead
 * (what it sees now; as it moves, the circle sweeps the strip it images).
 * A geostationary imager's full disk is its horizon. An instrument that
 * images nothing on the ground (GRACE's ranging, SDO's view of the Sun)
 * gets no circle.
 *
 * Written as pairs of Earth-fixed points (km), so both renderers draw it
 * as line segments like the marks of a past orbit.
 */
import { GLOBE_RADIUS_KM } from './satelliteOrbit'

export const FOOTPRINT_SEGMENTS = 48

/**
 * Cross-track swath, km, by instrument as the descriptors name them
 * (matched case-insensitively on the name's start). Sources: the
 * operators' instrument pages (VIIRS 3,060; MODIS 2,330; OLI/TIRS 185;
 * Sentinel-1 C-SAR interferometric wide 250; TROPOMI 2,600; GPM GMI 885,
 * DPR 245; AVHRR 2,900; Sentinel-2 MSI 290; OLCI 1,270; SLSTR 1,400;
 * AMSR2 1,450; ATMS 2,300). 0 = images nothing on the ground.
 */
export const SWATH_KM: Readonly<Record<string, number>> = {
  viirs: 3060,
  modis: 2330,
  oli: 185,
  tirs: 185,
  'c-sar': 250,
  sar: 250,
  tropomi: 2600,
  gmi: 885,
  dpr: 245,
  'gmi/dpr': 885,
  avhrr: 2900,
  msi: 290,
  olci: 1270,
  slstr: 1400,
  amsr2: 1450,
  atms: 2300,
  'k-band ranging system': 0,
  aia: 0,
  hmi: 0,
}

/** The swath of an instrument, km; null when unknown (the horizon is drawn), 0 when it images nothing on the ground. */
export function swathOf(instrument: string | null | undefined): number | null {
  if (!instrument) return null
  const key = instrument.trim().toLowerCase()
  if (key in SWATH_KM) return SWATH_KM[key]
  for (const [name, km] of Object.entries(SWATH_KM)) if (key.startsWith(name) || key.split(/[\s/,]+/).includes(name)) return km
  return null
}
/** Floats one footprint takes: `FOOTPRINT_SEGMENTS` pairs of points. */
export const FOOTPRINT_FLOATS = FOOTPRINT_SEGMENTS * 2 * 3

/**
 * Writes the horizon circle of a satellite at Earth-fixed (px, py, pz) km
 * into `out` from `offset`; NaN throughout when it is not above the ground.
 */
export function writeHorizonFootprint(px: number, py: number, pz: number, out: Float32Array, offset: number, swathKm: number | null = null): void {
  const r = Math.hypot(px, py, pz)
  if (!(r > GLOBE_RADIUS_KM + 1) || swathKm === 0) {
    out.fill(NaN, offset, offset + FOOTPRINT_FLOATS)
    return
  }
  // The horizon's central angle from the sub-satellite point; an instrument's swath caps it.
  let theta = Math.acos(GLOBE_RADIUS_KM / r)
  if (swathKm !== null && swathKm > 0) theta = Math.min(theta, swathKm / 2 / GLOBE_RADIUS_KM)
  writeGroundCircle(px / r, py / r, pz / r, theta, out, offset)
}

/** A ring on the ground of central angle `theta` around the unit direction n, as pairs of points. */
export function writeGroundCircle(nx: number, ny: number, nz: number, theta: number, out: Float32Array, offset: number): void {
  const n = [nx, ny, nz]
  // An east and a north on the ground under it (any pair across n will do; at the poles east is arbitrary).
  const lon = Math.atan2(n[1], n[0])
  const lat = Math.asin(Math.max(-1, Math.min(1, n[2])))
  const east = [-Math.sin(lon), Math.cos(lon), 0]
  const north = [-Math.sin(lat) * Math.cos(lon), -Math.sin(lat) * Math.sin(lon), Math.cos(lat)]
  const c = Math.cos(theta)
  const s = Math.sin(theta)
  let o = offset
  let prev: [number, number, number] | null = null
  for (let i = 0; i <= FOOTPRINT_SEGMENTS; i++) {
    const a = (i / FOOTPRINT_SEGMENTS) * Math.PI * 2
    const ca = Math.cos(a)
    const sa = Math.sin(a)
    const p: [number, number, number] = [0, 1, 2].map((k) => GLOBE_RADIUS_KM * (c * n[k] + s * (ca * east[k] + sa * north[k]))) as [number, number, number]
    if (prev) {
      out[o++] = prev[0]
      out[o++] = prev[1]
      out[o++] = prev[2]
      out[o++] = p[0]
      out[o++] = p[1]
      out[o++] = p[2]
    }
    prev = p
  }
}

/** The horizon's radius on the ground, km along the surface, for a satellite at distance r km from the centre. */
export function horizonRadiusKm(r: number): number {
  if (!(r > GLOBE_RADIUS_KM)) return 0
  return GLOBE_RADIUS_KM * Math.acos(GLOBE_RADIUS_KM / r)
}
